import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import colors from '@keyslabor/shared/theme/colors';
import { Box, Flex } from '@keyslabor/shared/ui';
import { useTranslation } from 'react-i18next';

export type RecordControlProps = {
  isRecording: boolean;
  isRecordReady: boolean;
  timeLeft: number;
  onRecordStart: () => void;
  onRecordStop: () => void;
  onShowRetryConfirm: () => void;
};

type RecordControlButtonProps = RecordControlProps & {
  isDisabled: boolean;
  /** Label shown while recording — the only part the variants disagree on. */
  recordingLabel: ReactNode;
};

const StylesRecordingButton = styled.button<{
  $isTryAgain: boolean;
  $isRecording: boolean;
}>`
  border-radius: 100%;
  width: 100px;
  height: 100px;
  border-width: 4px;
  border-style: solid;
  border-color: white;
  cursor: pointer;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: white;

  background-color: ${({ $isTryAgain, $isRecording }) => {
    if ($isTryAgain) {
      return colors.blueDeep;
    }

    return $isRecording ? colors.redCrimson : colors.greenSummer;
  }};

  &[disabled] {
    background-color: ${colors.grayDisabled};
  }
`;

export const RecordControlButton: FC<RecordControlButtonProps> = ({
  isRecording,
  isRecordReady,
  isDisabled,
  recordingLabel,
  onRecordStart,
  onRecordStop,
  onShowRetryConfirm,
}) => {
  const { t } = useTranslation();
  const isTryAgain = !isRecording && isRecordReady;

  const handlerClick = () => {
    if (isTryAgain) {
      onShowRetryConfirm();
    } else if (isRecording) {
      onRecordStop();
    } else {
      onRecordStart();
    }
  };

  const label = isRecording ? (
    recordingLabel
  ) : !isRecordReady ? (
    <>{t('record_button.start_record')}</>
  ) : (
    <Flex gap="0" flexDirection="column">
      <span style={{ height: '16px' }}>{t('record_button.try_again_ln1')}</span>
      <span>{t('record_button.try_again_ln2')}</span>
    </Flex>
  );

  return (
    <StylesRecordingButton
      $isRecording={isRecording}
      $isTryAgain={isTryAgain}
      onClick={handlerClick}
      disabled={isDisabled}
    >
      <Box
        textAlign="center"
        fontWeight="700"
        color="white"
        fontSize="16px"
        margin="0 auto"
        height="100%"
        css={css`
          & {
            display: -webkit-flex;
            display: flex;
            justify-content: center;
            -webkit-justify-content: center;
            align-items: center;
            -webkit-align-items: center;
          }
        `}
      >
        {label}
      </Box>
    </StylesRecordingButton>
  );
};

export default RecordControlButton;
